import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Copy, MessageSquareText, Share2, X } from 'lucide-react';
import { toast } from 'sonner';
import { HostBottomNav } from '../../components/HostBottomNav';
import { HostLimitedAccessCard } from '../../components/host/HostLimitedAccessCard';
import { HostAirbnbSettingsBanner } from '../../components/host/HostAirbnbSettingsBanner';
import InviteMessageEditor from '../../components/host/InviteMessageEditor';
import NotificationBellButton from '../../components/NotificationBellButton';
import { hostCardStyle } from '../../components/HostSettingsCard';
import WelcomeTourSheet from '../../components/WelcomeTourSheet';
import PageInfoButton from '../../components/PageInfoButton';
import { hostActiveAccessTourSlides } from '../../components/welcomeTourSlides';
import { hasSeenWelcomeTour, markWelcomeTourSeen } from '../../utils/welcomeTourState';
import { fetchMyHostListings, getListingPresentation } from '../../utils/hostListings';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getHostInvites,
  removeInvite,
  cancelHostInvite,
  syncHostDetectedReservations,
} from '../../utils/inviteApi';
import { formatEuropeanDate } from '../../utils/dateFormat';
import { shareOrCopy } from '../../utils/shareOrCopy';
import { trackInviteShared } from '../../utils/airbridge';
import { mintShareLink } from '../../utils/airbridgeShareLink';
import { buildShareUrl } from '../../utils/shareBaseUrl';
import { composeInviteShareText, DEFAULT_INVITE_SHARE_MESSAGE } from '../../utils/inviteShareMessage';
import { HostApprovedInviteCard } from '../../components/host/HostApprovedInviteCard';
import { HostDeactivateInviteSheet } from '../../components/host/HostDeactivateInviteSheet';

const buildHostInviteLink = (inviteId) => buildShareUrl(`/invite/${inviteId}`);

const SELECTED_LISTING_STORAGE_KEY = 'allure_host_home_selected_listing';

const listMotionProps = {
  layout: true,
  initial: { opacity: 0, y: 16, scale: 0.985 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -10, scale: 0.985 },
  transition: { duration: 0.24, ease: [0.25, 0.1, 0.25, 1] },
};

const getInviteListingMeta = (invite) => getListingPresentation({ title: invite.listing_name, address: invite.listing_address });

const ROUND_BUTTON_STYLE = {
  width: 44,
  height: 44,
  borderRadius: 999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--card-border)',
  background: 'var(--surface)',
  cursor: 'pointer',
  boxShadow: 'var(--premium-shadow-nested)',
  flexShrink: 0,
};

const InviteActionRow = ({ inviteId, onCopy, onShare, onRemove, copyTestId, shareTestId, removeTestId, shareColor }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
    <motion.button
      type="button"
      onClick={onRemove}
      data-testid={removeTestId}
      aria-label="Remove invite"
      style={{ ...ROUND_BUTTON_STYLE, color: '#DD0608', borderColor: 'rgba(221, 6, 8, 0.22)' }}
      whileHover={{ y: -1.5 }}
      whileTap={{ scale: 0.94 }}
    >
      <X size={20} />
    </motion.button>
    <motion.button
      type="button"
      onClick={() => onShare(inviteId)}
      data-testid={shareTestId}
      className="premium-pill-button"
      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, cursor: 'pointer', color: shareColor }}
      whileHover={{ y: -1.5 }}
      whileTap={{ scale: 0.985 }}
    >
      <Share2 size={18} /> Share Invite
    </motion.button>
    <motion.button
      type="button"
      onClick={() => onCopy(inviteId)}
      data-testid={copyTestId}
      aria-label="Copy invite link"
      style={{ ...ROUND_BUTTON_STYLE, color: 'var(--text)' }}
      whileHover={{ y: -1.5 }}
      whileTap={{ scale: 0.94 }}
    >
      <Copy size={18} />
    </motion.button>
  </div>
);

// Single-block preview shown on every invite card just above the action
// row. Two lines of the host's saved message (CSS `-webkit-line-clamp`
// keeps the height flat regardless of message length) plus a trailing
// "+ your invite link" line so the host can eyeball exactly what
// Share/Copy will paste into the guest's chat.
const InvitePreviewLine = ({ text, testId }) => (
  <div
    data-testid={testId}
    style={{
      fontFamily: "'Inria Serif', serif",
      fontSize: 12,
      lineHeight: 1.3,
      color: 'var(--text-3)',
      fontStyle: 'italic',
    }}
    title={text}
  >
    <p
      style={{
        margin: 0,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      Copies: “{text}”
    </p>
    <p style={{ margin: '2px 0 0', opacity: 0.85 }}>
      + your invite link
    </p>
  </div>
);

export default function HostActiveQRPage() {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [invites, setInvites] = useState([]);
  const [pageTourOpen, setPageTourOpen] = useState(false);
  const [hostListings, setHostListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState(() => {
    try {
      const stored = localStorage.getItem(SELECTED_LISTING_STORAGE_KEY);
      return stored || null;
    } catch { return null; }
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [messageEditorOpen, setMessageEditorOpen] = useState(false);
  const [deactivateInvite, setDeactivateInvite] = useState(null);
  const previousDetectedCountRef = useRef(0);
  const filterMenuRef = useRef(null);
  const isLimitedAccessHost = user?.host_status === 'rejected';

  // Persist the filter selection so navigating away and back returns the
  // host to the same view. Cleared value stores as empty string so we can
  // tell "explicitly all listings" from "never touched".
  const chooseListingFilter = useCallback((nextListingId) => {
    setSelectedListingId(nextListingId);
    try {
      if (nextListingId) localStorage.setItem(SELECTED_LISTING_STORAGE_KEY, nextListingId);
      else localStorage.removeItem(SELECTED_LISTING_STORAGE_KEY);
    } catch { /* noop */ }
  }, []);

  // The Share Invite button follows the current palette accent, except on
  // the signature Allure Red palette — there we flip it to green so the
  // primary CTA doesn't clash with the red identity of the card status
  // pill / warning tones.
  const shareInviteColor = palette === 'default' ? '#169C3A' : 'var(--accent-purple-strong)';

  // Flattened preview of the host's saved template — used by the
  // in-card `<InvitePreviewLine>` which CSS-clamps to 2 lines. We
  // collapse newlines into single spaces so a multi-paragraph
  // template still fills both visible lines instead of leaving the
  // second one blank when line 1 is short. Deliberately excludes the
  // invite URL — each card has its own unique link, and rendering the
  // same URL on every preview would be misleading. The "+ your invite
  // link" affordance below the message is provided by
  // `InvitePreviewLine` itself.
  const invitePreviewLine = useMemo(() => {
    const raw = (user?.invite_share_message || '').trim() || DEFAULT_INVITE_SHARE_MESSAGE;
    return raw.replace(/\s+/g, ' ').trim();
  }, [user?.invite_share_message]);

  useEffect(() => {
    if (!hasSeenWelcomeTour('host-page-active')) {
      setPageTourOpen(true);
    }
  }, []);

  const handleClosePageTour = () => {
    setPageTourOpen(false);
    markWelcomeTourSeen('host-page-active');
  };

  // Refresh user from /auth/me on mount so the Airbnb-settings banner
  // accurately reflects the latest `airbnb_settings_completed` value (it
  // can change in another route — e.g. after the host saves sync settings).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res?.data) return;
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
      })
      .catch(() => {});
  }, []);

  const refreshInvites = useCallback(async () => {
    try {
      if (isLimitedAccessHost) {
        setInvites([]);
        return;
      }
      await syncHostDetectedReservations();
      const response = await getHostInvites();
      setInvites(response);
    } catch (error) {
      console.error(error);
    }
  }, [isLimitedAccessHost]);

  // Fetch the host's current listings so the filter always mirrors what the
  // host actually owns right now. Newly-added listings show up automatically
  // on the next mount / refresh; removed ones drop out. We also silently
  // reset a stale selection to "All listings" if the previously-picked
  // listing has since been removed.
  const refreshHostListings = useCallback(async () => {
    if (isLimitedAccessHost) {
      setHostListings([]);
      return;
    }
    try {
      const listings = await fetchMyHostListings();
      setHostListings(listings);
    } catch (error) {
      /* keep whatever we already have — the filter is non-critical */
    }
  }, [isLimitedAccessHost]);

  useEffect(() => {
    refreshHostListings();
  }, [refreshHostListings]);

  // Close the filter dropdown on outside tap.
  useEffect(() => {
    if (!filterOpen) return undefined;
    const handler = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [filterOpen]);

  useEffect(() => {
    refreshInvites();
    const timer = setInterval(refreshInvites, 30000);
    return () => clearInterval(timer);
  }, [refreshInvites]);

  const today = useMemo(() => {
    const nextToday = new Date();
    nextToday.setHours(0, 0, 0, 0);
    return nextToday;
  }, []);

  const parseDate = (value) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  };

  // Sort by check-in date ASC so the reservation nearest to today is on top
  // and further-out ones are stacked underneath. Invites without a check-in
  // date (e.g. still-being-authored manual invites) sink to the bottom.
  const sortedInvites = useMemo(() => {
    const copy = [...invites];
    copy.sort((a, b) => {
      const aTime = a.check_in_date ? Date.parse(a.check_in_date) : Number.POSITIVE_INFINITY;
      const bTime = b.check_in_date ? Date.parse(b.check_in_date) : Number.POSITIVE_INFINITY;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return aTime - bTime;
    });
    return copy;
  }, [invites]);

  const listingFilteredInvites = useMemo(() => {
    if (!selectedListingId) return sortedInvites;
    return sortedInvites.filter((invite) => invite.listing_id === selectedListingId);
  }, [sortedInvites, selectedListingId]);

  // Auto-hide detected/manual invite cards once checkout passes.
  const openActivityInvites = useMemo(() => listingFilteredInvites.filter((invite) => {
    if (!['detected', 'confirmed_by_host'].includes(invite.status)) return false;
    const checkout = invite.checkout_date ? parseDate(invite.checkout_date) : null;
    return !checkout || today <= checkout;
  }), [listingFilteredInvites, today]);

  const openInvites = useMemo(() => listingFilteredInvites.filter((invite) => {
    if (invite.status !== 'approved') return false;
    const checkIn = parseDate(invite.check_in_date);
    const checkout = parseDate(invite.checkout_date);
    return checkIn && checkout && today >= checkIn && today <= checkout;
  }), [listingFilteredInvites, today]);

  const upcomingInvites = useMemo(() => listingFilteredInvites.filter((invite) => {
    if (invite.status !== 'approved') return false;
    const checkIn = parseDate(invite.check_in_date);
    return checkIn && today < checkIn;
  }), [listingFilteredInvites, today]);

  const selectedListing = useMemo(
    () => hostListings.find((listing) => listing.listingId === selectedListingId) || null,
    [hostListings, selectedListingId],
  );
  // Union of (1) real approved host listings and (2) any distinct
  // listing_id present in the invite feed. Point 2 keeps the filter alive
  // even when an invite carries a listing_id that no longer maps to a live
  // host_application — the host still sees that listing's name on the
  // invite card, so they should be able to filter by it. Order: real
  // listings first, then invite-only entries, both alphabetical.
  const filterableListings = useMemo(() => {
    const map = new Map();
    hostListings
      .filter((listing) => listing.status === 'approved')
      .forEach((listing) => { map.set(listing.listingId, listing); });
    invites.forEach((invite) => {
      if (!invite.listing_id || map.has(invite.listing_id)) return;
      map.set(invite.listing_id, {
        listingId: invite.listing_id,
        name: invite.listing_name || invite.listing_id,
        image: invite.listing_image || '',
        locationLabel: invite.listing_address || '',
        status: 'invite_only',
      });
    });
    return Array.from(map.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [hostListings, invites]);
  const filterButtonLabel = (() => {
    const entry = filterableListings.find((listing) => listing.listingId === selectedListingId);
    return entry?.name || selectedListing?.name || 'All listings';
  })();
  const showListingFilter = !isLimitedAccessHost && filterableListings.length > 1;

  // If the selected listing has fully disappeared (no matching host listing
  // AND no invite referencing it) revert silently to "All listings". Skip
  // while the data is still loading (both lists empty) so a stored
  // selection isn't wiped before invites/listings arrive.
  useEffect(() => {
    if (!selectedListingId) return;
    if (!hostListings.length && !invites.length) return;
    if (!filterableListings.some((listing) => listing.listingId === selectedListingId)) {
      chooseListingFilter(null);
    }
  }, [filterableListings, selectedListingId, hostListings.length, invites.length, chooseListingFilter]);

  useEffect(() => {
    if (openActivityInvites.length > previousDetectedCountRef.current && openActivityInvites[0]) {
      const newestReservation = openActivityInvites[0];
      const listingMeta = getInviteListingMeta(newestReservation);
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(newestReservation.source === 'manual' ? 'Manual invite ready' : 'New reservation detected', {
          body: `${listingMeta.displayName}${listingMeta.displayAddress ? ` · ${listingMeta.displayAddress}` : ''} · ${formatEuropeanDate(newestReservation.check_in_date)} — ${formatEuropeanDate(newestReservation.checkout_date)}`,
        });
      }
    }
    previousDetectedCountRef.current = openActivityInvites.length;
  }, [openActivityInvites]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const handleShareInvite = async (inviteId) => {
    const rawUrl = buildHostInviteLink(inviteId);
    // Fire the share sheet immediately with the raw URL — keep the
    // Web Share / Capacitor Share call in the user-activation window so
    // production WebViews don't reject with NotAllowedError. Mint the
    // Airbridge short link in the background purely for attribution.
    mintShareLink({
      inviteType: 'host_to_guest',
      inviteKey: inviteId,
      rawUrl,
      ogTitle: 'Your Allure invite',
      ogDescription: 'Tap to unlock your venue discounts in Allure.',
    }).catch(() => {});
    // Compose the full share body — host's saved message on top, unique
    // invite URL underneath. We pass the URL separately so native share
    // sheets that surface a dedicated "Link" slot (WhatsApp / iMessage)
    // still get the raw URL for preview cards.
    const shareText = composeInviteShareText(user?.invite_share_message, rawUrl);
    try {
      const result = await shareOrCopy({
        title: 'Your Allure invite',
        text: shareText,
        url: rawUrl,
      });
      if (result.method === 'share' && !result.cancelled) {
        trackInviteShared({ inviteId, inviteType: 'host_to_guest', channel: 'share_sheet' });
        toast.success('Invite shared');
      } else if (result.method === 'share' && result.cancelled) {
        toast.info('Share cancelled');
      } else if (result.method === 'copy') {
        trackInviteShared({ inviteId, inviteType: 'host_to_guest', channel: 'clipboard' });
        toast.success('Invite copied');
      } else {
        toast.error('Unable to share invite');
      }
    } catch {
      toast.error('Unable to share invite');
    }
  };

  const handleCopyInviteLink = async (inviteId) => {
    const rawUrl = buildHostInviteLink(inviteId);
    // Mint the Airbridge short link in the background purely for
    // attribution. The clipboard always gets the raw canonical
    // `allurexp.com/invite/<id>` URL so hosts (and their guests) see the
    // clean link instead of `abr.ge/…`.
    mintShareLink({
      inviteType: 'host_to_guest',
      inviteKey: inviteId,
      rawUrl,
    }).catch(() => {});
    // Prepend the host's saved invite message so the guest lands on the
    // link with context, not a bare URL.
    const shareText = composeInviteShareText(user?.invite_share_message, rawUrl);
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(shareText);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      trackInviteShared({ inviteId, inviteType: 'host_to_guest', channel: 'clipboard' });
      toast.success('Invite copied');
    } catch {
      toast.error('Unable to copy invite');
    }
  };

  const handleMessageSaved = (updatedUser) => {
    if (!updatedUser) return;
    setUser(updatedUser);
    try {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch { /* ignore quota errors */ }
  };

  const handleRemoveInvite = async (invite) => {
    // Optimistic remove — the previous `window.confirm` was blocked inside the
    // PWA/TWA shell so the card never went away. The small round X is a
    // deliberate target; treat the tap as a confirmed remove.
    setInvites((prev) => prev.filter((i) => i.invite_id !== invite.invite_id));
    try {
      await removeInvite(invite.invite_id);
      toast.success('Invite removed');
      await refreshInvites();
    } catch (error) {
      // Re-add on failure so the user sees their card return.
      await refreshInvites();
      toast.error(error.response?.data?.detail || 'Failed to remove invite');
    }
  };

  const renderDetectedCard = (invite) => {
    const listingImage = invite.listing_image || '/allure-logo-mono.png';
    const listingMeta = getInviteListingMeta(invite);
    const statusLabel = invite.source === 'manual' ? 'Manual invite' : 'Invite ready';

    return (
      <motion.div key={invite.invite_id} data-testid={`host-detected-reservation-card-${invite.invite_id}`} {...listMotionProps} style={{ ...hostCardStyle, background: 'var(--surface)', borderRadius: 24, padding: '14px 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <motion.span layout data-testid={`host-detected-reservation-status-${invite.invite_id}`} style={{ padding: '7px 13px', borderRadius: 9999, background: 'rgba(17, 181, 57, 0.14)', color: '#169C3A', fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.01em', flexShrink: 0 }}>
            {statusLabel}
          </motion.span>
          <p data-testid={`host-detected-reservation-dates-${invite.invite_id}`} style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text)', textAlign: 'right', lineHeight: 1.05 }}>
            {formatEuropeanDate(invite.check_in_date)} — {formatEuropeanDate(invite.checkout_date)}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 16 }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}>
            <img src={listingImage} alt={invite.listing_name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p data-testid={`host-detected-reservation-name-${invite.invite_id}`} style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 19, fontWeight: 700, color: 'var(--text)', lineHeight: 1.02 }}>
              {listingMeta.displayName}
            </p>
            {listingMeta.displayAddress ? <p data-testid={`host-detected-reservation-address-${invite.invite_id}`} style={{ margin: '6px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', lineHeight: 1.12 }}>{listingMeta.displayAddress}</p> : null}
            <div style={{ margin: '7px 0 0' }}>
              <InvitePreviewLine
                text={invitePreviewLine}
                testId={`host-detected-invite-preview-${invite.invite_id}`}
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <InviteActionRow
            inviteId={invite.invite_id}
            onCopy={handleCopyInviteLink}
            onShare={handleShareInvite}
            onRemove={() => handleRemoveInvite(invite)}
            copyTestId={`host-copy-detected-invite-${invite.invite_id}`}
            shareTestId={`host-share-detected-invite-${invite.invite_id}`}
            removeTestId={`host-remove-detected-invite-${invite.invite_id}`}
            shareColor={shareInviteColor}
          />
        </div>
      </motion.div>
    );
  };

  const handleMessageGuest = (invite) => {
    if (!invite?.conversation_id) {
      toast.error('No conversation yet');
      return;
    }
    navigate(`/host/chat/${invite.conversation_id}`);
  };

  const handleSoftCancelInvite = async (invite) => {
    await cancelHostInvite(invite.invite_id);
    toast.success('Invitation cancelled');
    await refreshInvites();
  };

  const handleRevokeInvite = async (invite) => {
    await removeInvite(invite.invite_id);
    toast.success('Access revoked');
    await refreshInvites();
  };

  const renderApprovedCard = (invite, section) => (
    <HostApprovedInviteCard
      key={invite.invite_id}
      invite={invite}
      today={today}
      section={section}
      onMessage={handleMessageGuest}
      onDeactivate={setDeactivateInvite}
    />
  );

  return (
    <div className="app-shell premium-page" data-testid="host-active-access-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton
          onClick={() => setPageTourOpen(true)}
          testId="host-active-page-info-button"
        />
        <NotificationBellButton buttonTestId="host-active-access-bell-button" popupTestId="host-active-access-notification-popup" />
      </div>
      <HostAirbnbSettingsBanner user={user} />
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 110, height: 96, objectFit: 'contain' }} />
        <h1 data-testid="host-active-access-title" style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 14 }}>Active Allure Access</h1>

        {isLimitedAccessHost ? (
          <div style={{ width: '100%', marginTop: 14 }}>
            <HostLimitedAccessCard onReferVenue={() => navigate('/host/refer-venue')} onInviteHosts={() => navigate('/host/referrals')} testIdPrefix="host-home-limited-access" />
          </div>
        ) : null}

        {showListingFilter ? (
          <div
            ref={filterMenuRef}
            data-testid="host-active-listing-filter"
            style={{ width: '100%', marginTop: 14, position: 'relative', zIndex: 20 }}
          >
            <motion.button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              data-testid="host-active-listing-filter-button"
              aria-haspopup="listbox"
              aria-expanded={filterOpen}
              className="premium-pill-button"
              whileHover={{ y: -1.5 }}
              whileTap={{ scale: 0.985 }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                padding: '11px 16px',
                fontFamily: "'Inria Serif', serif",
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--text)',
                cursor: 'pointer',
              }}
            >
              <span
                data-testid="host-active-listing-filter-label"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left', flex: 1 }}
              >
                {filterButtonLabel}
              </span>
              <motion.span
                animate={{ rotate: filterOpen ? 180 : 0 }}
                transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
                style={{ display: 'flex' }}
              >
                <ChevronDown size={18} strokeWidth={2.2} color="var(--text-2)" />
              </motion.span>
            </motion.button>

            <AnimatePresence>
              {filterOpen ? (
                <motion.div
                  key="listing-filter-menu"
                  data-testid="host-active-listing-filter-menu"
                  role="listbox"
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    background: 'var(--surface)',
                    borderRadius: 18,
                    border: '1px solid var(--card-border)',
                    boxShadow: 'var(--premium-shadow-main)',
                    padding: 6,
                    // Cap the dropdown at ~7 options before scrolling kicks
                    // in. Each row is ≈ 40px, so 7 rows + 6+6 outer padding
                    // ≈ 292px.
                    maxHeight: 292,
                    overflowY: 'auto',
                  }}
                >
                  {[{ listingId: null, name: 'All listings' }, ...filterableListings].map((option) => {
                    const active = option.listingId === selectedListingId;
                    const optionKey = option.listingId ?? '__all__';
                    return (
                      <motion.button
                        key={optionKey}
                        type="button"
                        onClick={() => {
                          chooseListingFilter(option.listingId);
                          setFilterOpen(false);
                        }}
                        data-testid={`host-active-listing-filter-option-${option.listingId ?? 'all'}`}
                        role="option"
                        aria-selected={active}
                        whileTap={{ scale: 0.985 }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          padding: '10px 12px',
                          border: 'none',
                          background: active ? 'var(--accent-surface-soft)' : 'transparent',
                          borderRadius: 12,
                          fontFamily: "'Inria Serif', serif",
                          fontSize: 14,
                          fontWeight: active ? 700 : 500,
                          color: active ? 'var(--accent-text-soft)' : 'var(--text)',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {option.name}
                        </span>
                        {active ? <Check size={16} strokeWidth={2.4} color="var(--accent-purple-strong)" /> : null}
                      </motion.button>
                    );
                  })}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : null}

        <div style={{ width: '100%', marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
            <h2 data-testid="host-detected-reservations-heading" style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Open activity... ({openActivityInvites.length})
            </h2>
            {!isLimitedAccessHost ? (
              <motion.button
                type="button"
                onClick={() => setMessageEditorOpen(true)}
                data-testid="host-edit-invite-message-button"
                aria-label="Edit invite message"
                whileHover={{ y: -1.5 }}
                whileTap={{ scale: 0.96 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 14px',
                  borderRadius: 999,
                  // Filled with the same palette-aware accent as the
                  // Share Invite CTA (green on Allure Red, purple/blue/etc
                  // on other palettes) so the button is instantly visible
                  // and never sits white-on-white in light mode.
                  border: 'none',
                  background: shareInviteColor,
                  color: '#FFFFFF',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: 'var(--premium-shadow-nested)',
                }}
              >
                <MessageSquareText size={15} strokeWidth={2.2} />
                <span>Edit message</span>
              </motion.button>
            ) : null}
          </div>
          <AnimatePresence initial={false} mode="popLayout">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {openActivityInvites.length ? openActivityInvites.map(renderDetectedCard) : <p data-testid="host-detected-reservations-empty" style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-3)', textAlign: 'center', padding: '6px 0 2px' }}>No open activity</p>}
            </div>
          </AnimatePresence>
        </div>

        <div style={{ width: '100%', marginTop: 18 }}>
          <h2 data-testid="host-open-invites-heading" style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Open invites... ({openInvites.length})</h2>
          <AnimatePresence initial={false} mode="popLayout">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {openInvites.length ? openInvites.map((invite) => renderApprovedCard(invite, 'open')) : <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-3)', textAlign: 'center' }}>No open invites</p>}
            </div>
          </AnimatePresence>
        </div>

        <div style={{ width: '100%', marginTop: 18 }}>
          <h2 data-testid="host-upcoming-invites-heading" style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Upcoming... ({upcomingInvites.length})</h2>
          <AnimatePresence initial={false} mode="popLayout">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {upcomingInvites.length ? upcomingInvites.map((invite) => renderApprovedCard(invite, 'upcoming')) : <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-3)', textAlign: 'center' }}>No upcoming invites</p>}
            </div>
          </AnimatePresence>
        </div>

      </div>
      <HostBottomNav />

      <WelcomeTourSheet
        open={pageTourOpen}
        onClose={handleClosePageTour}
        slides={hostActiveAccessTourSlides}
        testIdPrefix="host-active-page-tour"
      />

      <InviteMessageEditor
        open={messageEditorOpen}
        onClose={() => setMessageEditorOpen(false)}
        initialMessage={user?.invite_share_message || ''}
        onSaved={handleMessageSaved}
      />

      <HostDeactivateInviteSheet
        open={!!deactivateInvite}
        invite={deactivateInvite}
        onClose={() => setDeactivateInvite(null)}
        onSoftCancel={handleSoftCancelInvite}
        onRevoke={handleRevokeInvite}
      />
    </div>
  );
}
